import React, { useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ComplexEmailSidebar from './ComplexEmailSidebar';
import {
  Container, Wrapper, ComplexArtboardWrapper, DocumentContainer,
  ToolbarContainer
} from '../ComplexDocumentDesign/styles';
import { PagesWrapper } from './styles';
import withScrolling from '../../components/ScrollZone';
import ComplexDocumentHistoryProvider, { COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import ComplexArtboardProvider, { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import ComplexEmailProvider from '../../context/complexEmailContext';
import ComplexTextEditingProvider from '../../context/complexTextEditingContext';
import { COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../utils/complexDocumentConstants';
import BusinessLogicProvider from '../../context/businessLogicContext';
import ComplexEmailMenuBar from './ComplexEmailMenuBar';
import FontsProvider from '../../context/fontsContext';
import ComplexEmailArtboard from './ComplexEmailArtboard';
import { ModalPopupContainer } from '../ComplexDocumentDesign/ComplexPdfInserts/styles';
import { TabMenuItemContainer } from '../SidebarMenu/styles';
import { useVersionContext, VERSION_CONTEXT_KEYS, VersionContextProvider } from '../../context/complexVersionContext';

const OVERRIDE_DOCUMENT_CONTEXT_STATE = { [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE]: true };
const OVERRIDE_ARTBOARD_CONTEXT_STATE = { [CONTEXT_KEYS.ENABLE_RULER]: false };

const ComplexArtboardContainer = ({ TemplateId, pageWrapperRef }) => {
  const previewSelector = useCallback(store => store[CONTEXT_KEYS.IS_PREVIEW], []);
  const [isPreview] = useArtboardContext(previewSelector);
  const accessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY], []);
  const [accessibilityView] = useArtboardContext(accessibilitySelector);
  return (
    <div style={{ display: `${(isPreview || accessibilityView) ? 'none' : 'contents'}` }}>
      <ComplexEmailArtboard templateId={TemplateId} visibilityRef={pageWrapperRef} />
    </div>
  );
};

const ComplexDesignMenuBar = () => {
  const customCurrentBlockSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentEditingBlock] = useArtboardContext(customCurrentBlockSelector);

  const previewEnabledSelector = useCallback((store) => store[CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD], []);
  const [isPreviewEnabled] = useArtboardContext(previewEnabledSelector);

  const versionSettingsVisibleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
    [],
  );
  const [versionSettingsVisible] = useVersionContext(versionSettingsVisibleSelector);
  
  return (
    !currentEditingBlock && !isPreviewEnabled && !versionSettingsVisible && <ComplexEmailMenuBar />
  );
};

// TODO : Integrate with lock API to acquire lock on template
const ComplexEmailDesign = ({ t, customVersionState = {} }) => {
  const { templateId , flowId , blockId} = useParams();
  const TemplateId = templateId || flowId || blockId ;
  const pageWrapperRef = useRef(undefined);

  const ScrollingComponent = useMemo(() => withScrolling(PagesWrapper, pageWrapperRef), []);

  return (
    <Container>
      <Wrapper>
        <DndProvider backend={HTML5Backend} key={1} context={window}>
          <VersionContextProvider customState={customVersionState}>
            <ComplexDocumentHistoryProvider customState={OVERRIDE_DOCUMENT_CONTEXT_STATE}>
              <ComplexArtboardProvider documentId={TemplateId} customState={OVERRIDE_ARTBOARD_CONTEXT_STATE}>
                <ComplexEmailProvider>
                  <FontsProvider>
                    <BusinessLogicProvider>
                      <ComplexTextEditingProvider>
                        <ComplexEmailSidebar />
                        <ComplexArtboardWrapper>
                          <div className="moveable-label" />
                          <DocumentContainer>
                            <ToolbarContainer></ToolbarContainer>
                            <ScrollingComponent id={COMPLEX_DOCUMENT_PAGE_WRAPPER} tabIndex={-1}>
                              <div id="preview-portal"></div>
                              <ComplexArtboardContainer TemplateId={TemplateId} pageWrapperRef={pageWrapperRef}/>
                            </ScrollingComponent>
                            <TabMenuItemContainer style={{zIndex: 3001}}>
                              <ModalPopupContainer id="modal-popup-insert-container" />
                            </TabMenuItemContainer>
                            <ComplexDesignMenuBar/>
                          </DocumentContainer>
                        </ComplexArtboardWrapper>
                      </ComplexTextEditingProvider>
                    </BusinessLogicProvider>
                  </FontsProvider>
                </ComplexEmailProvider>
              </ComplexArtboardProvider>
            </ComplexDocumentHistoryProvider>
          </VersionContextProvider>
        </DndProvider>
      </Wrapper>
    </Container>
  );
};

export default ComplexEmailDesign;
